import React from 'react';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { motion } from 'framer-motion';
import { Phone, DollarSign, Truck, Clock, Check } from 'lucide-react';
import { scrollToCTAEvent } from './CarForm';
import { useAppContext } from '../context/AppContext';

export const BrandsAndVehicles: React.FC = () => {
  const { city, isServicePage } = useAppContext();
  
  const brandSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        }
      }
    ]
  };

  /* const vehicleSettings = {
    ...brandSettings,
    slidesToShow: 3,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  }; */

  const services = [
    {
      icon: DollarSign,
      title: "Migliori Offerte in Contanti",
      description: "Ricevi fino a 5,000€ per il tuo veicolo"
    },
    {
      icon: Truck,
      title: "Rimozione Gratuita",
      description: city ? `Rimozione gratuita dell'auto a ${city}` : "Rimozione gratuita dell'auto ovunque in Italia"
    },
    {
      icon: Clock,
      title: "Servizio in Giornata",
      description: "Processo rapido e senza problemi"
    },
    {
      icon: Check,
      title: "Qualsiasi Condizione",
      description: "Acquistiamo auto in qualsiasi condizione"
    }
  ];

  return (
    <>
      {/* Value Proposition Section */}
      <section className="py-16 bg-[#fdc751] text-gray-900 relative overflow-hidden">
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'url("/img/overlay1.png")',
          opacity: 0.3,
          backgroundSize: 'cover',
          mixBlendMode: 'exclusion',
          backgroundAttachment: 'fixed',
          zIndex: 0
        }}
      />
        <div className="container relative z-10 px-4 mx-auto">
          <div className="grid items-center grid-cols-1 gap-8 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-4xl font-bold">
                {city ? `Ottieni il Massimo per la Tua Auto a ${city}` : 'Ottieni il Massimo per la Tua Auto Oggi'}
              </h2>
              <p className="mb-6 text-xl">Acquistiamo tutti i tipi di veicoli in qualsiasi condizione:</p>
              <ul className="grid grid-cols-2 gap-4 mb-8">
                {['Auto', 'Camion', 'Furgoni', 'SUV', '4x4', 'Ute', 'Auto Danneggiate', 'Auto Vecchie'].map((item) => (
                  <li key={item} className="flex items-center">
                    <Check className="w-5 h-5 mr-2" />
                    <span>{item}</span>
                  </li>  
                ))}
              </ul>
              <div className="flex flex-col gap-4 sm:flex-row">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const heroSection = document.getElementById('hero-section');
                    if (heroSection) {
                      heroSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-8 py-3 font-semibold text-gray-900 transition-colors bg-white rounded-lg hover:bg-gray-100"
                >
                  {city ? `Ottieni un Preventivo Gratuito a ${city}` : 'Ottieni un Preventivo Gratuito'}
                </motion.button>
                <motion.a
                  href="tel:+61451326175"
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center justify-center px-8 py-3 font-semibold text-white transition-colors bg-green-500 rounded-lg hover:bg-green-600"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Chiama Ora
                </motion.a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-6"
            >
              {services.map((service) => (
                <div key={service.title} className="p-6 rounded-lg bg-white/10 backdrop-blur-sm">
                  <service.icon className="w-10 h-10 mb-4" />
                  <h3 className="mb-2 text-xl font-semibold">{service.title}</h3>
                  <p>{service.description}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Simple Responsive Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }} 
            className="mt-16 w-full max-w-[1200px] mx-auto"
          >
            <img
              src="/img/top_dollar_offer.png"
              alt="Top Cash Offer for Your Car"
              className="w-full h-auto"
            />
          </motion.div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="relative py-20 bg-gray-50">
        <div className="container relative z-10 px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
             <span className="text-[#fdc751] font-semibold text-lg mb-2 block">QUALSIAISI MARCA</span>
            <h2 className="mt-2 text-3xl font-bold">
              {city ? `Acquistiamo Tutte le Marche a ${city}` : 'Acquistiamo Tutte le Marche'}
            </h2>
            <p className="mt-4 text-gray-600">
              {city 
                ? `Ottieni offerte in contanti immediate per il tuo veicolo a ${city}, indipendentemente dalla marca o dalle condizioni`
                : 'Ottieni offerte in contanti immediate per il tuo veicolo, indipendentemente dalla marca o dalle condizioni'
              }
            </p>
          </motion.div>

          <Slider {...brandSettings} className="brand-slider">
            {[
              { img: '/brands/fiat.png', name: 'Fiat' },
              { img: '/brands/volkswagen.png', name: 'Volkswagen' },
              { img: '/brands/peugeot.png', name: 'Peugeot' },
              { img: '/brands/renault.png', name: 'Renault' },
              { img: '/brands/bmw.png', name: 'BMW' },
              { img: '/brands/mercedes-benz.png', name: 'Mercedes-Benz' },
              { img: '/brands/audi.png', name: 'Audi' },
              { img: '/brands/toyota.png', name: 'Toyota' },
              { img: '/brands/ford.png', name: 'Ford' },
              { img: '/brands/opel.png', name: 'Opel' },
              { img: '/brands/alfa-romeo.png', name: 'Alfa Romeo' },
              { img: '/brands/citroen.png', name: 'Citroën' },
            ].map((brand) => (
              <motion.div
                key={brand.name}
                whileHover={{ scale: 1.05 }}
                className="px-4"
              >
                <div className="p-6 bg-white ">
                  <img 
                    src={brand.img} 
                    alt={brand.name}
                    className="object-contain w-32 h-32 mx-auto"
                  />
                  <h3 className="mt-4 text-lg font-semibold text-center">{brand.name}</h3>
                  {/* <p className="text-sm text-center text-gray-600">{brand.models}</p> */}
                </div>
              </motion.div>
            ))}
          </Slider>
        </div>
      </section>

      {/* Recent Purchases Section */}
      {/*  <section className="relative py-20 bg-white">
        <div className="container relative z-10 px-4 mx-auto">
          <motion.div  
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <span className="text-lg font-semibold text-blue-600">Transazioni Recenti</span>
            <h2 className="mt-2 text-3xl font-bold">Auto Acquistate di Recente</h2>
            <p className="mt-4 text-gray-600"> 
              See some of our recent purchases - we buy vehicles in any condition
            </p>
          </motion.div>

          <Slider {...vehicleSettings} className="vehicle-slider">
            {[
              {
                name: 'Toyota HiLux',
                img: '/img/vehicles/hilux.png',
                year: '2021',
                mileage: '45,000 km',
                transmission: 'Automatic',
                condition: 'Used'
              },
              {
                name: 'Mazda CX-5',
                img: '/img/vehicles/cx5.png',
                year: '2020',
                mileage: '38,000 km',
                transmission: 'Automatic',
                condition: 'Damaged'
              },
              {
                name: 'Ford Ranger',
                img: '/img/vehicles/ranger.png',
                year: '2022',
                mileage: '25,000 km',
                transmission: 'Automatic',
                condition: 'Like New'
              },
              {
                name: 'Hyundai Tucson',
                img: '/img/vehicles/tucson.png',
                year: '2021',
                mileage: '32,000 km',
                transmission: 'Automatic',
                condition: 'Used'
              }
            ].map((vehicle) => (
              <motion.div
                key={vehicle.name}
                whileHover={{ scale: 1.03 }}
                className="px-4"
              >
                <div className="overflow-hidden bg-white rounded-lg shadow-lg">
                  <div className="relative pb-[60%]">
                    <img 
                      src={vehicle.img} 
                      alt={vehicle.name}
                      className="absolute top-0 left-0 object-cover w-full h-full"
                    />
                    <div className="absolute px-3 py-1 text-sm text-white bg-blue-600 rounded-full top-4 right-4">
                      {vehicle.condition}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="mb-3 text-xl font-semibold">{vehicle.name}</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center">
                        <span className="w-24">Year:</span>
                        <span className="font-medium">{vehicle.year}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-24">Mileage:</span>
                        <span className="font-medium">{vehicle.mileage}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-24">Transmission:</span>
                        <span className="font-medium">{vehicle.transmission}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </Slider>
        </div>
      </section> */} 
    </>
  );
}; 